import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabase';
import SchoolSidebar from './SchoolSidebar';
import { SchoolMobileHeader } from './SchoolMobileHeader';
import BranchSelector from './BranchSelector';
import { useBranchFilter } from '../../hooks/useBranchFilter';
import './SchoolDashboard.css';
import './FeeSchedule.css';

const getDefaultEditForm = () => ({
    amount: '',
    currency: 'PKR',
    fee_type: 'monthly',
    description: '',
    due_date: ''
  });

const getDefaultClassFeeForm = () => ({
    class_id: '',
    amount: '',
    currency: 'PKR',
    fee_type: 'monthly',
    description: '',
    first_fee_date: '',
    expiry_duration: '30'
  });

const getDefaultAdditionalFees = () => ({
    selectedTypes: [],
    fees: {
      extracurricular: { amount: '', period: 'monthly', name: 'Extracurricular Activities' },
      associated_charges: { amount: '', period: 'monthly', name: 'Associated Charges' },
      other: { amount: '', period: 'monthly', name: '' }
    }
  });

function FeeSchedule() {
  const navigate = useNavigate();
  const [schoolData, setSchoolData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { selectedBranchId, handleBranchChange } = useBranchFilter();
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [academicYear, setAcademicYear] = useState({
    start_date: '',
    end_date: '',
    year_name: ''
  });
  const [feeSchedule, setFeeSchedule] = useState([]);
  const [classFees, setClassFees] = useState({});
  const [classAdditionalFees, setClassAdditionalFees] = useState([]); // Store additional fees for the selected class
  const [editingMonth, setEditingMonth] = useState(null);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editForm, setEditForm] = useState(getDefaultEditForm());

  // Edit class fee details state
  const [showEditClassFeeModal, setShowEditClassFeeModal] = useState(false);
  const [classFeeForm, setClassFeeForm] = useState(getDefaultClassFeeForm());
  const [additionalFees, setAdditionalFees] = useState(getDefaultAdditionalFees());
  const [feePlan, setFeePlan] = useState([]);
  const [editingClassFeeId, setEditingClassFeeId] = useState(null);
  
  // Currency options
  const currencyOptions = [
    { code: 'PKR', name: 'Pakistani Rupee', symbol: '₨' },
    { code: 'USD', name: 'US Dollar', symbol: '$' },
    { code: 'EUR', name: 'Euro', symbol: '€' },
    { code: 'GBP', name: 'British Pound', symbol: '£' },
    { code: 'AED', name: 'UAE Dirham', symbol: 'د.إ' },
    { code: 'SAR', name: 'Saudi Riyal', symbol: '﷼' },
    { code: 'QAR', name: 'Qatari Riyal', symbol: '﷼' },
    { code: 'KWD', name: 'Kuwaiti Dinar', symbol: 'د.ك' },
    { code: 'BHD', name: 'Bahraini Dinar', symbol: 'د.ب' },
    { code: 'OMR', name: 'Omani Rial', symbol: '﷼' },
    { code: 'JOD', name: 'Jordanian Dinar', symbol: 'د.ا' },
    { code: 'LBP', name: 'Lebanese Pound', symbol: 'ل.ل' },
    { code: 'EGP', name: 'Egyptian Pound', symbol: '£' },
    { code: 'ILS', name: 'Israeli Shekel', symbol: '₪' },
    { code: 'TRY', name: 'Turkish Lira', symbol: '₺' },
    { code: 'IRR', name: 'Iranian Rial', symbol: '﷼' },
    { code: 'IQD', name: 'Iraqi Dinar', symbol: 'د.ع' },
    { code: 'AFN', name: 'Afghan Afghani', symbol: '؋' },
    { code: 'INR', name: 'Indian Rupee', symbol: '₹' },
    { code: 'CAD', name: 'Canadian Dollar', symbol: 'C$' },
    { code: 'AUD', name: 'Australian Dollar', symbol: 'A$' }
  ];

  useEffect(() => {
    // Get school data from localStorage
    const storedSchoolData = localStorage.getItem('schoolData');
    if (storedSchoolData) {
      setSchoolData(JSON.parse(storedSchoolData));
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!schoolData?.id) return;

    const loadClasses = async () => {
      const classesData = await fetchClasses();
      setSelectedClass(prev => {
        if (!prev) return prev;
        const exists = classesData.some(cls => cls.id === prev);
        return exists ? prev : '';
      });
    };

    loadClasses();
      fetchAcademicYear();
  }, [schoolData?.id, selectedBranchId]);

  useEffect(() => {
    setSelectedClass('');
    setFeeSchedule([]);
    setClassFees({});
    setClassAdditionalFees([]);
    setShowEditForm(false);
    setShowEditClassFeeModal(false);
    setEditingMonth(null);
    setEditingClassFeeId(null);
    setEditForm(getDefaultEditForm());
    setClassFeeForm(getDefaultClassFeeForm());
    setAdditionalFees(getDefaultAdditionalFees());
    setFeePlan([]);
  }, [selectedBranchId]);

  useEffect(() => {
    if (selectedClass && schoolData?.id) {
      fetchFeeSchedule();
      fetchClassFees();
      fetchClassAdditionalFees();
    }
  }, [selectedClass, schoolData?.id]);

  useEffect(() => {
    setSelectedClass('');
    setFeeSchedule([]);
    setClassFees({});
    setClassAdditionalFees([]);
    setShowEditForm(false);
    setShowEditClassFeeModal(false);
    setEditingMonth(null);
    setEditingClassFeeId(null);
    setEditForm(getDefaultEditForm());
    setClassFeeForm(getDefaultClassFeeForm());
    setAdditionalFees(getDefaultAdditionalFees());
    setFeePlan([]);
  }, [selectedBranchId]);

  // Regenerate schedule when feeSchedule, classFees, or additional fees change
  useEffect(() => {
    if (selectedClass && academicYear.start_date && academicYear.end_date) {
      console.log('Regenerating schedule due to data change');
      console.log('feeSchedule:', feeSchedule);
      console.log('classFees:', classFees);
      console.log('classAdditionalFees:', classAdditionalFees);
    }
  }, [feeSchedule, classFees, classAdditionalFees, selectedClass, academicYear.start_date, academicYear.end_date]);
  
  // Update fee plan when form changes in edit modal
  useEffect(() => {
    if (showEditClassFeeModal && classFeeForm.fee_type && classFeeForm.first_fee_date && classFeeForm.amount && 
        academicYear.start_date && academicYear.end_date) {
      const plan = generateFeePlan(
        classFeeForm.fee_type,
        classFeeForm.first_fee_date,
        academicYear.start_date,
        academicYear.end_date,
        classFeeForm.amount,
        additionalFees
      );
      setFeePlan(plan);
    } else {
      setFeePlan([]);
    }
  }, [showEditClassFeeModal, classFeeForm.fee_type, classFeeForm.first_fee_date, classFeeForm.amount, 
      academicYear.start_date, academicYear.end_date, additionalFees]);

  const fetchClasses = async () => {
    try {
      let query = supabase
        .from('classes')
        .select('*')
        .eq('school_id', schoolData.id)
        .eq('is_active', true);

      if (selectedBranchId != null) {
        query = query.eq('branch_id', selectedBranchId);
      }

      const { data, error } = await query.order('class_name', { ascending: true });

      if (error) {
        console.error('Error fetching classes:', error);
        return;
      }

      setClasses(data || []);
      return data || [];
    } catch (err) {
      console.error('Error:', err);
      return [];
    }
  };

  const fetchAcademicYear = async () => {
    try {
      const { data, error } = await supabase
        .from('academic_years')
        .select('*')
        .eq('school_id', schoolData.id)
        .eq('is_active', true)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching academic year:', error);
        return;
      }

      if (data) {
        setAcademicYear({
          start_date: data.start_date || '',
          end_date: data.end_date || '',
          year_name: data.year_name || ''
        });
      }
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const fetchClassFees = async () => {
    if (!selectedClass) return;
    
    try {
      const { data, error } = await supabase
        .from('class_fees')
        .select(`
          *,
          classes(class_name)
        `)
        .eq('school_id', schoolData.id)
        .eq('class_id', selectedClass)
        .eq('is_active', true);

      if (error) {
        console.error('Error fetching class fees:', error);
        return;
      }

      // Convert to object for easy lookup
      const feesObj = {};
      data?.forEach(fee => {
        feesObj[fee.id] = fee;
      });
      setClassFees(feesObj);
    } catch (err) {
      console.error('Error:', err);
    }
  };
  
  const fetchClassAdditionalFees = async () => {
    if (!selectedClass || !schoolData?.id) return;
    
    try {
      const { data, error } = await supabase
        .from('additional_class_fees')
        .select('*')
        .eq('school_id', schoolData.id)
        .eq('class_id', selectedClass)
        .eq('is_active', true);
      
      if (error) {
        console.error('Error fetching additional fees:', error);
        setClassAdditionalFees([]);
        return;
      }
      
      setClassAdditionalFees(data || []);
    } catch (err) {
      console.error('Error:', err);
      setClassAdditionalFees([]);
    }
  };
  
  const fetchAdditionalFees = async (classId) => {
    if (!classId) return;
    
    try {
      const { data, error } = await supabase
        .from('additional_class_fees')
        .select('*')
        .eq('school_id', schoolData.id)
        .eq('class_id', classId)
        .eq('is_active', true);
      
      if (error) {
        console.error('Error fetching additional fees:', error);
        return;
      }
      
      if (data && data.length > 0) {
        const selectedTypes = data.map(fee => fee.fee_category);
        const fees = {
          extracurricular: { amount: '', period: 'monthly', name: 'Extracurricular Activities' },
          associated_charges: { amount: '', period: 'monthly', name: 'Associated Charges' },
          other: { amount: '', period: 'monthly', name: '' }
        };
        
        data.forEach(fee => {
          fees[fee.fee_category] = {
            amount: fee.amount.toString(),
            period: fee.period,
            name: fee.fee_name || (fee.fee_category === 'extracurricular' ? 'Extracurricular Activities' : fee.fee_category === 'associated_charges' ? 'Associated Charges' : '')
          };
        });
        
        setAdditionalFees({
          selectedTypes,
          fees
        });
      } else {
        setAdditionalFees({
          selectedTypes: [],
          fees: {
            extracurricular: { amount: '', period: 'monthly', name: 'Extracurricular Activities' },
            associated_charges: { amount: '', period: 'monthly', name: 'Associated Charges' },
            other: { amount: '', period: 'monthly', name: '' }
          }
        });
      }
    } catch (err) {
      console.error('Error fetching additional fees:', err);
    }
  };
  
  const handleEditClassFeeDetails = async () => {
    if (!selectedClass) return;
    
    try {
      // Fetch class fee
      const { data: feeData, error: feeError } = await supabase
        .from('class_fees')
        .select('*')
        .eq('school_id', schoolData.id)
        .eq('class_id', selectedClass)
        .eq('is_active', true)
        .single();
      
      if (feeError && feeError.code !== 'PGRST116') {
        console.error('Error fetching class fee:', feeError);
        alert('Failed to load class fee details.');
        return;
      }
      
      if (feeData) {
        setEditingClassFeeId(feeData.id);
        setClassFeeForm({
          class_id: selectedClass,
          amount: feeData.amount.toString(),
          currency: feeData.currency || 'PKR',
          fee_type: feeData.fee_type || 'monthly',
          description: feeData.description || '',
          first_fee_date: feeData.first_fee_date || '',
          expiry_duration: feeData.expiry_duration?.toString() || '30'
        });
      } else {
        // If no fee exists, initialize with selected class
        setEditingClassFeeId(null);
        setClassFeeForm({
          class_id: selectedClass,
          amount: '',
          currency: 'PKR',
          fee_type: 'monthly',
          description: '',
          first_fee_date: '',
          expiry_duration: '30'
        });
      }
      
      // Fetch additional fees
      await fetchAdditionalFees(selectedClass);
      
      setShowEditClassFeeModal(true);
    } catch (err) {
      console.error('Error:', err);
      alert('Failed to load fee details.');
    }
  };
  
  const generateFeePlan = (feeType, firstFeeDate, startDate, endDate, amount, additionalFeesData) => {
    const plan = [];
    const start = new Date(startDate);
    const yearEnd = new Date(endDate);
    
    // Calculate additional fees
    const monthlyAdditionalFees = additionalFeesData.selectedTypes
      .filter(type => additionalFeesData.fees[type].period === 'monthly')
      .reduce((sum, type) => sum + (parseFloat(additionalFeesData.fees[type].amount) || 0), 0);
    
    const oneTimeAdditionalFees = additionalFeesData.selectedTypes
      .filter(type => additionalFeesData.fees[type].period === 'one_time')
      .reduce((sum, type) => sum + (parseFloat(additionalFeesData.fees[type].amount) || 0), 0);
    
    const monthlyAmountForQuarterly = feeType === 'quarterly' ? parseFloat(amount) / 3 : 0;
    const monthlyAmountForSemester = feeType === 'semester' ? parseFloat(amount) / 6 : 0;
    const monthlyAmountForAnnual = feeType === 'annual' ? parseFloat(amount) / 12 : 0;
    
    let currentDate = new Date(startDate);
    let quarterlyCounter = 0;
    let semesterCounter = 0;
    let annualCounter = 0;
    
    switch (feeType) {
      case 'monthly':
        while (currentDate <= yearEnd) {
          const baseAmount = parseFloat(amount);
          const additionalMonthly = monthlyAdditionalFees;
          const isFirstPayment = plan.length === 0;
          const oneTimeFee = isFirstPayment ? oneTimeAdditionalFees : 0;
          
          plan.push({
            date: new Date(currentDate),
            amount: baseAmount + additionalMonthly + oneTimeFee,
            baseAmount: baseAmount,
            additionalMonthly: additionalMonthly,
            oneTimeFee: oneTimeFee,
            type: 'Monthly Fee',
            breakdown: {
              tuition: baseAmount,
              monthlyAdditional: additionalMonthly,
              oneTimeAdditional: oneTimeFee
            }
          });
          currentDate.setMonth(currentDate.getMonth() + 1);
        }
        break;
        
      case 'quarterly':
        while (currentDate <= yearEnd) {
          const monthInQuarter = quarterlyCounter % 3;
          let baseAmount = 0;
          let quarterlyFeeLabel = '';
          
          if (monthInQuarter === 0) {
            baseAmount = parseFloat(amount);
            quarterlyFeeLabel = 'Quarterly Fee';
          } else {
            baseAmount = monthlyAmountForQuarterly;
            quarterlyFeeLabel = `Monthly (Quarterly/${3})`;
          }
          
          const additionalMonthly = monthlyAdditionalFees;
          const isFirstPayment = plan.length === 0;
          const oneTimeFee = isFirstPayment ? oneTimeAdditionalFees : 0;
          
          plan.push({
            date: new Date(currentDate),
            amount: baseAmount + additionalMonthly + oneTimeFee,
            baseAmount: baseAmount,
            additionalMonthly: additionalMonthly,
            oneTimeFee: oneTimeFee,
            type: quarterlyFeeLabel,
            breakdown: {
              tuition: baseAmount,
              monthlyAdditional: additionalMonthly,
              oneTimeAdditional: oneTimeFee
            }
          });
          
          currentDate.setMonth(currentDate.getMonth() + 1);
          quarterlyCounter++;
        }
        break;
        
      case 'semester':
        while (currentDate <= yearEnd) {
          const monthInSemester = semesterCounter % 6;
          let baseAmount = 0;
          let semesterFeeLabel = '';
          
          if (monthInSemester === 0) {
            baseAmount = parseFloat(amount);
            semesterFeeLabel = 'Semester Fee';
          } else {
            baseAmount = monthlyAmountForSemester;
            semesterFeeLabel = `Monthly (Semester/${6})`;
          }
          
          const additionalMonthly = monthlyAdditionalFees;
          const isFirstPayment = plan.length === 0;
          const oneTimeFee = isFirstPayment ? oneTimeAdditionalFees : 0;
          
          plan.push({
            date: new Date(currentDate),
            amount: baseAmount + additionalMonthly + oneTimeFee,
            baseAmount: baseAmount,
            additionalMonthly: additionalMonthly,
            oneTimeFee: oneTimeFee,
            type: semesterFeeLabel,
            breakdown: {
              tuition: baseAmount,
              monthlyAdditional: additionalMonthly,
              oneTimeAdditional: oneTimeFee
            }
          });
          
          currentDate.setMonth(currentDate.getMonth() + 1);
          semesterCounter++;
        }
        break;
        
      case 'annual':
        while (currentDate <= yearEnd) {
          const monthInYear = annualCounter % 12;
          let baseAmount = 0;
          let annualFeeLabel = '';
          
          if (monthInYear === 0) {
            baseAmount = parseFloat(amount);
            annualFeeLabel = 'Annual Fee';
          } else {
            baseAmount = monthlyAmountForAnnual;
            annualFeeLabel = `Monthly (Annual/${12})`;
          }
          
          const additionalMonthly = monthlyAdditionalFees;
          const isFirstPayment = plan.length === 0;
          const oneTimeFee = isFirstPayment ? oneTimeAdditionalFees : 0;
          
          plan.push({
            date: new Date(currentDate),
            amount: baseAmount + additionalMonthly + oneTimeFee,
            baseAmount: baseAmount,
            additionalMonthly: additionalMonthly,
            oneTimeFee: oneTimeFee,
            type: annualFeeLabel,
            breakdown: {
              tuition: baseAmount,
              monthlyAdditional: additionalMonthly,
              oneTimeAdditional: oneTimeFee
            }
          });
          
          currentDate.setMonth(currentDate.getMonth() + 1);
          annualCounter++;
        }
        break;
        
      default:
        break;
    }
    
    return plan;
  };
  
  const handleUpdateClassFee = async (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    
    try {
      // Validate required fields
      if (!classFeeForm.class_id) {
        alert('Please select a class.');
        return;
      }
      
      if (!classFeeForm.amount || parseFloat(classFeeForm.amount) <= 0) {
        alert('Please enter a valid fee amount.');
        return;
      }
      
      if (!classFeeForm.fee_type) {
        alert('Please select a fee type.');
        return;
      }
      
      if (!classFeeForm.first_fee_date) {
        alert('Please select the first fee date.');
        return;
      }
      
      if (!classFeeForm.expiry_duration) {
        alert('Please select expiry duration.');
        return;
      }

      // Update or insert main class fee
      let error;
      let result;
      
      if (editingClassFeeId) {
        // Update existing fee
        console.log('Updating class fee with ID:', editingClassFeeId);
        console.log('Update data:', {
          amount: parseFloat(classFeeForm.amount),
          currency: classFeeForm.currency,
          fee_type: classFeeForm.fee_type,
          description: classFeeForm.description,
          first_fee_date: classFeeForm.first_fee_date,
          expiry_duration: parseInt(classFeeForm.expiry_duration),
        });
        
        result = await supabase
          .from('class_fees')
          .update({
            amount: parseFloat(classFeeForm.amount),
            currency: classFeeForm.currency,
            fee_type: classFeeForm.fee_type,
            description: classFeeForm.description || null,
            first_fee_date: classFeeForm.first_fee_date,
            expiry_duration: parseInt(classFeeForm.expiry_duration),
            is_active: true,
            updated_at: new Date().toISOString()
          })
          .eq('id', editingClassFeeId);
        error = result.error;
      } else {
        // Insert new fee
        console.log('Inserting new class fee');
        console.log('Insert data:', {
          school_id: schoolData.id,
          class_id: classFeeForm.class_id,
          amount: parseFloat(classFeeForm.amount),
          currency: classFeeForm.currency,
          fee_type: classFeeForm.fee_type,
          description: classFeeForm.description || null,
          first_fee_date: classFeeForm.first_fee_date,
          expiry_duration: parseInt(classFeeForm.expiry_duration),
        });
        
        result = await supabase
          .from('class_fees')
          .insert({
            school_id: schoolData.id,
            class_id: classFeeForm.class_id,
            amount: parseFloat(classFeeForm.amount),
            currency: classFeeForm.currency,
            fee_type: classFeeForm.fee_type,
            description: classFeeForm.description || null,
            first_fee_date: classFeeForm.first_fee_date,
            expiry_duration: parseInt(classFeeForm.expiry_duration),
            is_active: true,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          });
        error = result.error;
      }

      if (error) {
        console.error('Error updating class fee:', error);
        console.error('Error details:', JSON.stringify(error, null, 2));
        alert(`Failed to update class fee: ${error.message || 'Unknown error'}. Please check the console for details.`);
        return;
      }
      
      console.log('Class fee updated/inserted successfully');

      // Update additional fees if any are selected
      if (additionalFees.selectedTypes.length > 0 && classFeeForm.class_id) {
        const additionalFeesToSave = additionalFees.selectedTypes.map(feeType => ({
          school_id: schoolData.id,
          class_id: classFeeForm.class_id,
          fee_category: feeType,
          fee_name: additionalFees.fees[feeType].name || (feeType === 'extracurricular' ? 'Extracurricular Activities' : feeType === 'associated_charges' ? 'Associated Charges' : additionalFees.fees.other.name),
          amount: parseFloat(additionalFees.fees[feeType].amount) || 0,
          period: additionalFees.fees[feeType].period,
          currency: classFeeForm.currency,
          is_active: true,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }));

        // Delete existing additional fees for this class first, then insert new ones
        const { error: deleteError } = await supabase
          .from('additional_class_fees')
          .delete()
          .eq('school_id', schoolData.id)
          .eq('class_id', classFeeForm.class_id);

        if (deleteError) {
          console.error('Error deleting existing additional fees:', deleteError);
        }

        const { error: additionalError } = await supabase
          .from('additional_class_fees')
          .insert(additionalFeesToSave);

        if (additionalError) {
          console.error('Error saving additional fees:', additionalError);
          alert('Class fee updated, but failed to save additional fees. Please try again.');
        }
      } else {
        // If no additional fees selected, delete all existing ones
        const { error: deleteError } = await supabase
          .from('additional_class_fees')
          .delete()
          .eq('school_id', schoolData.id)
          .eq('class_id', classFeeForm.class_id);
        
        if (deleteError) {
          console.error('Error deleting additional fees:', deleteError);
        }
      }

      alert('Class fee updated successfully!');
      setShowEditClassFeeModal(false);
      setEditingClassFeeId(null);
      
      // Refresh data
      await fetchClassFees();
      await fetchAdditionalFees(selectedClass);
      await fetchFeeSchedule();
      
      // Reset form
      setClassFeeForm({
        class_id: selectedClass,
        amount: '',
        currency: 'PKR',
        fee_type: 'monthly',
        description: '',
        first_fee_date: '',
        expiry_duration: '30'
      });
      setAdditionalFees({
        selectedTypes: [],
        fees: {
          extracurricular: { amount: '', period: 'monthly', name: 'Extracurricular Activities' },
          associated_charges: { amount: '', period: 'monthly', name: 'Associated Charges' },
          other: { amount: '', period: 'monthly', name: '' }
        }
      });
    } catch (err) {
      console.error('Error in handleUpdateClassFee:', err);
      console.error('Error stack:', err.stack);
      alert(`Failed to update class fee: ${err.message || 'Unknown error'}. Please check the console for details.`);
    }
  };

  const fetchFeeSchedule = async () => {
    if (!selectedClass || !schoolData?.id) return;
    
    try {
      console.log('Fetching fee schedule for school:', schoolData.id, 'class:', selectedClass);
      const { data, error } = await supabase
        .from('fee_schedule')
        .select(`
          *,
          classes(class_name),
          class_fees(amount, currency, fee_type)
        `)
        .eq('school_id', schoolData.id)
        .eq('class_id', selectedClass)
        .eq('is_active', true)
        .order('fee_date', { ascending: true });

      if (error) {
        console.error('Error fetching fee schedule:', error);
        return;
      }

      console.log('Fetched fee schedule data:', data);
      setFeeSchedule(data || []);
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const generateMonthlySchedule = () => {
    if (!academicYear.start_date || !academicYear.end_date || !selectedClass) {
      return [];
    }

    const schedule = [];
    const startDate = new Date(academicYear.start_date);
    const endDate = new Date(academicYear.end_date);
    
    // Get the class fee information
    const classFee = Object.values(classFees)[0];
    if (!classFee) return [];

    let currentDate = new Date(startDate);
    
    while (currentDate <= endDate) {
      const monthStart = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      const monthEnd = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
      
      // Find if there's a scheduled fee for this month
      const scheduledFee = feeSchedule.find(fee => {
        const feeDate = new Date(fee.fee_date);
        return feeDate.getMonth() === currentDate.getMonth() && 
               feeDate.getFullYear() === currentDate.getFullYear();
      });
      
      if (scheduledFee) {
        console.log(`Found scheduled fee for ${currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}:`, scheduledFee);
      }

      // Calculate period start and end dates based on first_fee_date and expiry_duration
      let periodStart = monthStart; // Default to month start
      let periodEnd = monthEnd; // Default to month end
      let dueDate = monthStart; // Default to month start
      
      if (classFee.first_fee_date) {
        const firstFeeDate = new Date(classFee.first_fee_date);
        const expiryDuration = classFee.expiry_duration || 30;
        
        // Calculate period start and end for this specific month
        if (currentDate.getMonth() === firstFeeDate.getMonth() && 
            currentDate.getFullYear() === firstFeeDate.getFullYear()) {
          // This is the first month - use first_fee_date as period start
          periodStart = new Date(firstFeeDate);
          periodEnd = new Date(firstFeeDate);
          periodEnd.setDate(periodEnd.getDate() + expiryDuration - 1);
          dueDate = new Date(firstFeeDate);
          dueDate.setDate(dueDate.getDate() + expiryDuration);
        } else {
          // For subsequent months, calculate based on fee type
          const monthsFromStart = (currentDate.getFullYear() - firstFeeDate.getFullYear()) * 12 + 
                                 (currentDate.getMonth() - firstFeeDate.getMonth());
          
          switch (classFee.fee_type) {
            case 'monthly':
              periodStart = new Date(firstFeeDate);
              periodStart.setMonth(periodStart.getMonth() + monthsFromStart);
              periodEnd = new Date(periodStart);
              periodEnd.setDate(periodEnd.getDate() + expiryDuration - 1);
              dueDate = new Date(periodStart);
              dueDate.setDate(dueDate.getDate() + expiryDuration);
              break;
            case 'quarterly':
              periodStart = new Date(firstFeeDate);
              periodStart.setMonth(periodStart.getMonth() + (monthsFromStart * 3));
              periodEnd = new Date(periodStart);
              periodEnd.setDate(periodEnd.getDate() + expiryDuration - 1);
              dueDate = new Date(periodStart);
              dueDate.setDate(dueDate.getDate() + expiryDuration);
              break;
            case 'semester':
              periodStart = new Date(firstFeeDate);
              periodStart.setMonth(periodStart.getMonth() + (monthsFromStart * 6));
              periodEnd = new Date(periodStart);
              periodEnd.setDate(periodEnd.getDate() + expiryDuration - 1);
              dueDate = new Date(periodStart);
              dueDate.setDate(dueDate.getDate() + expiryDuration);
              break;
            case 'annual':
              if (monthsFromStart === 0) {
                periodStart = new Date(firstFeeDate);
                periodEnd = new Date(firstFeeDate);
                periodEnd.setDate(periodEnd.getDate() + expiryDuration - 1);
                dueDate = new Date(firstFeeDate);
                dueDate.setDate(dueDate.getDate() + expiryDuration);
              } else {
                // Annual fee only applies to the first month
                continue;
              }
              break;
          }
        }
      }

      // Calculate additional fees
      const monthlyAdditionalFees = classAdditionalFees
        .filter(fee => fee.period === 'monthly')
        .reduce((sum, fee) => sum + (parseFloat(fee.amount) || 0), 0);
      
      // One-time fees only apply to the first month
      const isFirstMonth = schedule.length === 0;
      const oneTimeAdditionalFees = isFirstMonth 
        ? classAdditionalFees
            .filter(fee => fee.period === 'one_time')
            .reduce((sum, fee) => sum + (parseFloat(fee.amount) || 0), 0)
        : 0;
      
      // Use scheduled fee data if available, otherwise use class fee defaults
      const baseFeeAmount = scheduledFee ? scheduledFee.amount : classFee.amount;
      const finalFeeAmount = baseFeeAmount + monthlyAdditionalFees + oneTimeAdditionalFees;
      const finalCurrency = scheduledFee ? scheduledFee.currency : classFee.currency;
      const finalFeeType = scheduledFee ? scheduledFee.fee_type : classFee.fee_type;
      
      // For scheduled fees, use the scheduled fee date as due date
      const finalDueDate = scheduledFee ? new Date(scheduledFee.fee_date) : dueDate;

      schedule.push({
        month: currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
        monthStart: monthStart,
        monthEnd: monthEnd,
        periodStart: periodStart,
        periodEnd: periodEnd,
        dueDate: finalDueDate,
        feeAmount: finalFeeAmount,
        baseFeeAmount: baseFeeAmount,
        additionalFees: monthlyAdditionalFees + oneTimeAdditionalFees,
        currency: finalCurrency,
        feeType: finalFeeType,
        scheduledFee: scheduledFee,
        isScheduled: !!scheduledFee,
        expiryDuration: classFee.expiry_duration || 30
      });

      currentDate.setMonth(currentDate.getMonth() + 1);
    }

    return schedule;
  };

  const handleEditMonth = (month) => {
    setEditingMonth(month);
    setEditForm({
      amount: month.feeAmount?.toString() || '',
      currency: month.currency || 'PKR',
      fee_type: month.feeType || 'monthly',
      description: month.scheduledFee?.description || '',
      due_date: month.dueDate.toISOString().split('T')[0] // Convert to YYYY-MM-DD format
    });
    setShowEditForm(true);
  };

  const handleUpdateMonthFee = async (e) => {
    e.preventDefault();
    try {
      console.log('Updating fee for month:', editingMonth?.month);
      console.log('Form data:', editForm);
      console.log('Current feeSchedule:', feeSchedule);
      
      // Check if this month already has a scheduled fee
      const existingFee = feeSchedule.find(fee => {
        const feeDate = new Date(fee.fee_date);
        return feeDate.getMonth() === editingMonth.monthStart.getMonth() && 
               feeDate.getFullYear() === editingMonth.monthStart.getFullYear();
      });

      console.log('Existing fee found:', existingFee);

      if (existingFee) {
        // Update existing scheduled fee
        console.log('Updating existing fee with ID:', existingFee.id);
        const { data, error } = await supabase
          .from('fee_schedule')
          .update({
            amount: parseFloat(editForm.amount),
            currency: editForm.currency,
            fee_type: editForm.fee_type,
            description: editForm.description,
            fee_date: editForm.due_date,
            updated_at: new Date().toISOString()
          })
          .eq('id', existingFee.id)
          .select();

        if (error) {
          console.error('Error updating fee schedule:', error);
          alert('Failed to update fee schedule. Please try again.');
          return;
        }
        console.log('Update successful:', data);
      } else {
        // Create new scheduled fee
        console.log('Creating new scheduled fee');
        const { data, error } = await supabase
          .from('fee_schedule')
          .insert({
            school_id: schoolData.id,
            class_id: selectedClass,
            amount: parseFloat(editForm.amount),
            currency: editForm.currency,
            fee_type: editForm.fee_type,
            description: editForm.description,
            fee_date: editForm.due_date,
            is_active: true,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          })
          .select();

        if (error) {
          console.error('Error creating fee schedule:', error);
          alert('Failed to create fee schedule. Please try again.');
          return;
        }
        console.log('Insert successful:', data);
      }

      alert('Fee schedule updated successfully!');
      setShowEditForm(false);
      setEditingMonth(null);
      setEditForm({
        amount: '',
        currency: 'PKR',
        fee_type: 'monthly',
        description: '',
        due_date: ''
      });
      
      // Refresh both fee schedule and class fees
      await fetchFeeSchedule();
      await fetchClassFees();
    } catch (err) {
      console.error('Error:', err);
      alert('Failed to update fee schedule. Please try again.');
    }
  };

  const handleCancelEdit = () => {
    setShowEditForm(false);
    setEditingMonth(null);
    setEditForm({
      amount: '',
      currency: 'PKR',
      fee_type: 'monthly',
      description: '',
      due_date: ''
    });
  };

  const handleLogout = () => {
    navigate('/schoollogin');
  };

  if (loading) {
    return (
      <div className="school-dashboard">
        <div className="loading-container">
          <div className="loading-spinner">⏳</div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (!schoolData) {
    return (
      <div className="school-dashboard">
        <div className="error-container">
          <h2>Access Denied</h2>
          <p>Please log in to access the school dashboard.</p>
          <button onClick={() => navigate('/schoollogin')} className="btn-primary">
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  const selectedClassName = classes.find(cls => cls.id === selectedClass)?.class_name || '';
  const monthlySchedule = generateMonthlySchedule();

  return (
    <div className="school-dashboard">
      <SchoolMobileHeader 
        schoolData={schoolData} 
        mobileMenuOpen={mobileMenuOpen} 
        setMobileMenuOpen={setMobileMenuOpen} 
        handleLogout={handleLogout} 
      />
      
      {/* Sidebar */}
      <SchoolSidebar schoolData={schoolData} onLogout={handleLogout} />
      
      <div className="main-content">
        <div className="content-header">
          <h1>📋 Fee Schedule</h1>
          <p>View complete fee schedule for each class</p>
        </div>
        <div className="branch-filter-row">
          <BranchSelector
            schoolId={schoolData?.id}
            selectedBranchId={selectedBranchId}
            onBranchChange={handleBranchChange}
          />
          <span className="branch-context-text">
            {selectedBranchId
              ? 'Showing fee schedule for the selected branch'
              : 'Showing fee schedule for all branches'}
          </span>
        </div>

        <div className="fee-schedule-container">
          {/* Academic Year Display */}
          {academicYear.year_name && (
            <div className="academic-year-info">
              <h3>📅 Current Academic Year: {academicYear.year_name}</h3>
              <p>
                <strong>Start Date:</strong> {new Date(academicYear.start_date).toLocaleDateString()} | 
                <strong> End Date:</strong> {new Date(academicYear.end_date).toLocaleDateString()}
              </p>
            </div>
          )}

          {/* Class Selection */}
          <div className="class-selection-section">
            <div className="section-card">
              <h2>📚 Select Class</h2>
              <div className="class-selector">
                <div>
                <select
                  value={selectedClass}
                  onChange={(e) => setSelectedClass(e.target.value)}
                  className="class-select"
                >
                  <option value="">Choose a class to view fee schedule...</option>
                  {classes.map((cls) => (
                    <option key={cls.id} value={cls.id}>
                      {cls.class_name}
                    </option>
                  ))}
                </select>
                </div>
                {selectedClass && (
                  <button
                    type="button"
                    onClick={handleEditClassFeeDetails}
                    className="edit-details-btn"
                  >
                    ✏️ Edit Details
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Edit Form Modal */}
          {showEditForm && (
            <div className="edit-form-modal">
              <div className="edit-form-overlay" onClick={handleCancelEdit}></div>
              <div className="edit-form-container">
                <div className="edit-form-header">
                  <h3>✏️ Edit Fee for {editingMonth?.month}</h3>
                  <button onClick={handleCancelEdit} className="close-btn">×</button>
                </div>
                
                <form onSubmit={handleUpdateMonthFee} className="edit-form">
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="edit-amount">Fee Amount *</label>
                      <input
                        type="number"
                        id="edit-amount"
                        value={editForm.amount}
                        onChange={(e) => setEditForm({...editForm, amount: e.target.value})}
                        className="form-input"
                        required
                        min="0"
                        step="0.01"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="edit-currency">Currency *</label>
                      <select
                        id="edit-currency"
                        value={editForm.currency}
                        onChange={(e) => setEditForm({...editForm, currency: e.target.value})}
                        className="form-select"
                        required
                      >
                        <option value="PKR">PKR</option>
                        <option value="USD">USD</option>
                        <option value="EUR">EUR</option>
                        <option value="GBP">GBP</option>
                      </select>
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="edit-fee-type">Fee Type *</label>
                      <select
                        id="edit-fee-type"
                        value={editForm.fee_type}
                        onChange={(e) => setEditForm({...editForm, fee_type: e.target.value})}
                        className="form-select"
                        required
                      >
                        <option value="monthly">Monthly</option>
                        <option value="quarterly">Quarterly</option>
                        <option value="semester">Semester</option>
                        <option value="annual">Annual</option>
                      </select>
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="edit-due-date">Due Date *</label>
                      <input
                        type="date"
                        id="edit-due-date"
                        value={editForm.due_date}
                        onChange={(e) => setEditForm({...editForm, due_date: e.target.value})}
                        className="form-input"
                        required
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="edit-description">Description</label>
                    <textarea
                      id="edit-description"
                      value={editForm.description}
                      onChange={(e) => setEditForm({...editForm, description: e.target.value})}
                      className="form-textarea"
                      rows="3"
                      placeholder="Enter fee description..."
                    />
                  </div>

                  <div className="form-actions">
                    <button type="submit" className="btn-primary">
                      💾 Update Fee
                    </button>
                    <button type="button" onClick={handleCancelEdit} className="btn-secondary">
                      ❌ Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Fee Schedule Display */}
          {selectedClass && (
            <div className="schedule-display-section">
              <div className="section-card">
                <h2>📅 Fee Schedule for {selectedClassName}</h2>
                
                {monthlySchedule.length > 0 ? (
                  <div className="schedule-grid">
                    {monthlySchedule.map((month, index) => (
                      <div key={index} className={`schedule-month ${month.isScheduled ? 'scheduled' : 'default'}`}>
                        <div className="month-header">
                          <h3>{month.month}</h3>
                          <span className={`fee-status ${month.isScheduled ? 'scheduled' : 'default'}`}>
                            {month.isScheduled ? '📋 Scheduled' : '💰 Default'}
                          </span>
                        </div>
                        
                        <div className="month-details">
                          <div className="date-range">
                            <span className="date-label">Period:</span>
                            <span className="date-value">
                              {month.periodStart.toLocaleDateString()} - {month.periodEnd.toLocaleDateString()}
                            </span>
                          </div>
                          
                          <div className="fee-info">
                            <div className="fee-amount">
                              <span className="currency">{month.currency}</span>
                              <span className="amount">{month.feeAmount?.toLocaleString()}</span>
                            </div>
                            <div className="fee-type">
                              {month.feeType?.charAt(0).toUpperCase() + month.feeType?.slice(1)} Fee
                            </div>
                          </div>
                          
                          <div className="due-date-info">
                            <div className="due-date">
                              <strong>Due Date:</strong> {month.dueDate.toLocaleDateString()}
                            </div>
                            <div className="expiry-info">
                              <strong>Expires After:</strong> {month.expiryDuration} days
                            </div>
                            {month.scheduledFee && month.scheduledFee.description && (
                              <div className="scheduled-description">
                                <strong>Description:</strong> {month.scheduledFee.description}
                              </div>
                            )}
                          </div>
                          
                          <div className="month-actions">
                            <button 
                              onClick={() => handleEditMonth(month)}
                              className="btn-edit-month"
                              disabled={showEditForm}
                            >
                              ✏️ Edit Month
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="no-schedule">
                    <p>No fee schedule found for this class. Please set up fees first.</p>
                  </div>
                )}

                {/* Summary */}
                {monthlySchedule.length > 0 && (
                  <div className="schedule-summary">
                    <h3>📊 Schedule Summary</h3>
                    <div className="summary-grid">
                      <div className="summary-item">
                        <span className="summary-label">Total Months:</span>
                        <span className="summary-value">{monthlySchedule.length}</span>
                      </div>
                      <div className="summary-item">
                        <span className="summary-label">Scheduled Fees:</span>
                        <span className="summary-value">{monthlySchedule.filter(m => m.isScheduled).length}</span>
                      </div>
                      <div className="summary-item">
                        <span className="summary-label">Default Fees:</span>
                        <span className="summary-value">{monthlySchedule.filter(m => !m.isScheduled).length}</span>
                      </div>
                      <div className="summary-item total">
                        <span className="summary-label">Total Amount:</span>
                        <span className="summary-value">
                          {monthlySchedule[0]?.currency} {monthlySchedule.reduce((sum, month) => sum + (month.feeAmount || 0), 0).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Edit Class Fee Details Modal */}
      {showEditClassFeeModal && (
        <div className="popup-overlay" onClick={() => setShowEditClassFeeModal(false)}>
          <div className="popup-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '1000px', maxHeight: '90vh', overflowY: 'auto' }}>
            <div className="popup-header" style={{ backgroundColor: '#FF8C42', borderRadius: '8px 8px 0 0' }}>
              <h3 style={{ color: '#FFFFFF', margin: 0 }}>Edit Class Fee Details</h3>
              <button className="close-btn" onClick={() => setShowEditClassFeeModal(false)} style={{ color: '#FFFFFF' }}>
                ×
              </button>
            </div>
            
            <div style={{ padding: '2rem' }}>
              <form onSubmit={handleUpdateClassFee} className="class-fee-form">
                {/* Academic Year Display */}
                {academicYear.year_name && (
                  <div className="academic-year-info" style={{ marginBottom: '1.5rem', padding: '1rem', background: '#F0FDF4', borderRadius: '8px', border: '1px solid #BBF7D0' }}>
                    <h3 style={{ margin: 0, marginBottom: '0.5rem', color: '#065F46' }}>📅 Current Academic Year: {academicYear.year_name}</h3>
                    <p style={{ margin: 0, color: '#047857' }}>
                      <strong>Start Date:</strong> {new Date(academicYear.start_date).toLocaleDateString()} | 
                      <strong> End Date:</strong> {new Date(academicYear.end_date).toLocaleDateString()}
                    </p>
                  </div>
                )}

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="edit-class-select">Select Class *</label>
                    <select
                      id="edit-class-select"
                      value={classFeeForm.class_id}
                      disabled
                      className="form-select"
                      style={{ backgroundColor: '#F3F4F6', cursor: 'not-allowed' }}
                    >
                      <option value="">Choose a class...</option>
                      {classes.map((cls) => (
                        <option key={cls.id} value={cls.id}>
                          {cls.class_name}
                        </option>
                      ))}
                    </select>
                    <small style={{ color: '#6B7280', fontSize: '0.85rem' }}>Class cannot be changed</small>
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="edit-fee-amount">Fee Amount *</label>
                    <div className="amount-input-container">
                      <input
                        type="number"
                        id="edit-fee-amount"
                        value={classFeeForm.amount}
                        onChange={(e) => setClassFeeForm({...classFeeForm, amount: e.target.value})}
                        placeholder="Enter amount"
                        className="form-input amount-input"
                        min="0"
                        step="0.01"
                        required
                      />
                      <select
                        value={classFeeForm.currency}
                        onChange={(e) => setClassFeeForm({...classFeeForm, currency: e.target.value})}
                        className="currency-select"
                        required
                      >
                        {currencyOptions.map((currency) => (
                          <option key={currency.code} value={currency.code}>
                            {currency.code} - {currency.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="edit-fee-type">Fee Type *</label>
                    <select
                      id="edit-fee-type"
                      value={classFeeForm.fee_type}
                      onChange={(e) => setClassFeeForm({...classFeeForm, fee_type: e.target.value})}
                      className="form-select"
                      required
                    >
                      <option value="monthly">Monthly</option>
                      <option value="quarterly">Quarterly</option>
                      <option value="semester">Semester</option>
                      <option value="annual">Annual</option>
                    </select>
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="edit-first-fee-date">First Date of Fee *</label>
                    <input
                      type="date"
                      id="edit-first-fee-date"
                      value={classFeeForm.first_fee_date}
                      onChange={(e) => setClassFeeForm({...classFeeForm, first_fee_date: e.target.value})}
                      className="form-input"
                      required
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="edit-expiry-duration">Expiry Duration (Days) *</label>
                    <select
                      id="edit-expiry-duration"
                      value={classFeeForm.expiry_duration}
                      onChange={(e) => setClassFeeForm({...classFeeForm, expiry_duration: e.target.value})}
                      className="form-select"
                      required
                    >
                      <option value="7">7 Days</option>
                      <option value="15">15 Days</option>
                      <option value="30">30 Days</option>
                      <option value="45">45 Days</option>
                      <option value="60">60 Days</option>
                      <option value="90">90 Days</option>
                      <option value="120">120 Days</option>
                      <option value="180">180 Days</option>
                      <option value="365">365 Days (1 Year)</option>
                    </select>
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="edit-expiry-info">Expiry Information</label>
                    <div className="expiry-info-display">
                      <p>Fees will expire after <strong>{classFeeForm.expiry_duration} days</strong> from the due date</p>
                      <p className="expiry-note">After expiry, students will need to pay late fees or penalties</p>
                    </div>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="edit-fee-description">Description</label>
                  <textarea
                    id="edit-fee-description"
                    value={classFeeForm.description}
                    onChange={(e) => setClassFeeForm({...classFeeForm, description: e.target.value})}
                    placeholder="Additional details about the fee..."
                    className="form-textarea"
                    rows="3"
                  />
                </div>

                {/* Fee Plan Preview */}
                {feePlan.length > 0 && (
                  <div className="fee-plan-preview" style={{ marginTop: '1.5rem', padding: '1.5rem', background: '#F9FAFB', borderRadius: '8px', border: '1px solid #E5E7EB' }}>
                    <h3 style={{ marginBottom: '1rem', color: '#1F2937' }}>📋 Fee Plan Preview (Combined Monthly Breakdown)</h3>
                    <p className="plan-description" style={{ marginBottom: '1rem', color: '#6B7280' }}>
                      Based on your selections, here's the complete fee schedule for the academic year with all fees combined:
                    </p>
                    <div className="fee-plan-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '1rem' }}>
                      {feePlan.map((item, index) => (
                        <div key={index} className="fee-plan-item" style={{ textAlign: 'left', padding: '1rem', background: '#FFFFFF', borderRadius: '8px', border: '1px solid #E5E7EB' }}>
                          <div className="fee-date" style={{ marginBottom: '0.5rem', fontWeight: '600', color: '#1F2937' }}>
                            {item.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          </div>
                          <div className="fee-amount" style={{ fontSize: '1.2rem', fontWeight: '700', color: '#059669', marginBottom: '0.75rem' }}>
                            {classFeeForm.currency} {item.amount.toLocaleString()}
                          </div>
                          <div className="fee-type" style={{ fontSize: '0.85rem', color: '#6B7280', marginBottom: '0.5rem', textTransform: 'none' }}>
                            {item.type}
                          </div>
                          {item.breakdown && (
                            <div className="fee-breakdown" style={{ 
                              marginTop: '0.75rem', 
                              paddingTop: '0.75rem', 
                              borderTop: '1px solid #E5E7EB',
                              fontSize: '0.8rem',
                              color: '#4B5563'
                            }}>
                              <div style={{ marginBottom: '0.25rem' }}>
                                <span style={{ fontWeight: '500' }}>Tuition:</span> {classFeeForm.currency} {item.breakdown.tuition.toLocaleString()}
                              </div>
                              {item.breakdown.monthlyAdditional > 0 && (
                                <div style={{ marginBottom: '0.25rem' }}>
                                  <span style={{ fontWeight: '500' }}>Monthly Additional:</span> {classFeeForm.currency} {item.breakdown.monthlyAdditional.toLocaleString()}
                                </div>
                              )}
                              {item.breakdown.oneTimeAdditional > 0 && (
                                <div style={{ marginBottom: '0.25rem', color: '#DC2626', fontWeight: '500' }}>
                                  <span>One-Time Fee:</span> {classFeeForm.currency} {item.breakdown.oneTimeAdditional.toLocaleString()}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                    <div className="total-summary" style={{ 
                      background: '#ECFDF5', 
                      border: '2px solid #10B981', 
                      borderRadius: '8px', 
                      padding: '1rem', 
                      textAlign: 'center',
                      marginTop: '1rem'
                    }}>
                      <strong style={{ fontSize: '1.1rem', color: '#047857' }}>
                        Total Fees: {classFeeForm.currency} {feePlan.reduce((sum, item) => sum + item.amount, 0).toLocaleString()}
                      </strong>
                    </div>
                  </div>
                )}

                {/* Add Other Fields Section */}
                <div className="additional-fees-section" style={{ marginTop: '2rem', paddingTop: '2rem', borderTop: '2px solid #E5E7EB' }}>
                  <h2 style={{ marginBottom: '1rem', color: '#1F2937' }}>Add Other Fields</h2>
                  <p className="section-description" style={{ marginBottom: '1.5rem', color: '#6B7280' }}>
                    Add additional fees such as extracurricular activities, associated charges, or custom fees.
                  </p>

                  {/* Fee Type Selection */}
                  <div className="form-group" style={{ marginBottom: '1.5rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.75rem', fontWeight: '600', color: '#374151' }}>
                      Select Fee Types (Multiple Selection)
                    </label>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                      {[
                        { value: 'extracurricular', label: 'Extracurricular Activities' },
                        { value: 'associated_charges', label: 'Associated Charges' },
                        { value: 'other', label: 'Other' }
                      ].map((option) => (
                        <label
                          key={option.value}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.75rem',
                            padding: '0.75rem',
                            border: '1px solid #D1D5DB',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            backgroundColor: additionalFees.selectedTypes.includes(option.value) ? '#EFF6FF' : '#FFFFFF',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            if (!additionalFees.selectedTypes.includes(option.value)) {
                              e.currentTarget.style.backgroundColor = '#F9FAFB';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!additionalFees.selectedTypes.includes(option.value)) {
                              e.currentTarget.style.backgroundColor = '#FFFFFF';
                            }
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={additionalFees.selectedTypes.includes(option.value)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setAdditionalFees(prev => ({
                                  ...prev,
                                  selectedTypes: [...prev.selectedTypes, option.value]
                                }));
                              } else {
                                setAdditionalFees(prev => ({
                                  ...prev,
                                  selectedTypes: prev.selectedTypes.filter(t => t !== option.value),
                                  fees: {
                                    ...prev.fees,
                                    [option.value]: {
                                      amount: '',
                                      period: 'monthly',
                                      name: option.value === 'other' ? '' : prev.fees[option.value].name
                                    }
                                  }
                                }));
                              }
                            }}
                            style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                          />
                          <span style={{ fontWeight: '500', color: '#1F2937' }}>{option.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Fee Details for Selected Types */}
                  {additionalFees.selectedTypes.length > 0 && (
                    <div className="additional-fees-details" style={{ marginTop: '2rem' }}>
                      <h3 style={{ marginBottom: '1rem', color: '#374151', fontSize: '1.1rem' }}>Fee Details</h3>
                      {additionalFees.selectedTypes.map((feeType) => (
                        <div
                          key={feeType}
                          style={{
                            padding: '1.5rem',
                            border: '1px solid #E5E7EB',
                            borderRadius: '8px',
                            marginBottom: '1rem',
                            backgroundColor: '#FAFAFA'
                          }}
                        >
                          <h4 style={{ marginBottom: '1rem', color: '#1F2937', fontSize: '1rem' }}>
                            {feeType === 'extracurricular' ? 'Extracurricular Activities' :
                             feeType === 'associated_charges' ? 'Associated Charges' : 'Other Fee'}
                          </h4>

                          {/* Name field for "Other" */}
                          {feeType === 'other' && (
                            <div className="form-group" style={{ marginBottom: '1rem' }}>
                              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151' }}>
                                Fee Name <span style={{ color: '#EF4444' }}>*</span>
                              </label>
                              <input
                                type="text"
                                value={additionalFees.fees.other.name}
                                onChange={(e) => setAdditionalFees(prev => ({
                                  ...prev,
                                  fees: {
                                    ...prev.fees,
                                    other: { ...prev.fees.other, name: e.target.value }
                                  }
                                }))}
                                placeholder="Enter fee name"
                                style={{
                                  width: '100%',
                                  padding: '0.75rem',
                                  border: '1px solid #D1D5DB',
                                  borderRadius: '6px',
                                  fontSize: '0.95rem'
                                }}
                                required
                              />
                            </div>
                          )}

                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                            {/* Fee Amount */}
                            <div className="form-group">
                              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151' }}>
                                Fee Amount <span style={{ color: '#EF4444' }}>*</span>
                              </label>
                              <input
                                type="number"
                                value={additionalFees.fees[feeType].amount}
                                onChange={(e) => setAdditionalFees(prev => ({
                                  ...prev,
                                  fees: {
                                    ...prev.fees,
                                    [feeType]: { ...prev.fees[feeType], amount: e.target.value }
                                  }
                                }))}
                                placeholder="Enter amount"
                                min="0"
                                step="0.01"
                                style={{
                                  width: '100%',
                                  padding: '0.75rem',
                                  border: '1px solid #D1D5DB',
                                  borderRadius: '6px',
                                  fontSize: '0.95rem'
                                }}
                                required
                              />
                            </div>

                            {/* Period Selection */}
                            <div className="form-group">
                              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151' }}>
                                Period <span style={{ color: '#EF4444' }}>*</span>
                              </label>
                              <select
                                value={additionalFees.fees[feeType].period}
                                onChange={(e) => setAdditionalFees(prev => ({
                                  ...prev,
                                  fees: {
                                    ...prev.fees,
                                    [feeType]: { ...prev.fees[feeType], period: e.target.value }
                                  }
                                }))}
                                style={{
                                  width: '100%',
                                  padding: '0.75rem',
                                  border: '1px solid #D1D5DB',
                                  borderRadius: '6px',
                                  fontSize: '0.95rem',
                                  backgroundColor: '#FFFFFF'
                                }}
                                required
                              >
                                <option value="monthly">Monthly</option>
                                <option value="one_time">1 Time</option>
                              </select>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Save Button */}
                  <div className="form-actions" style={{ marginTop: '2rem', paddingTop: '1.5rem', borderTop: '2px solid #E5E7EB', display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                    <button 
                      type="button" 
                      onClick={() => setShowEditClassFeeModal(false)}
                      style={{
                        padding: '0.75rem 2rem',
                        fontSize: '1rem',
                        fontWeight: '600',
                        background: '#F3F4F6',
                        color: '#374151',
                        border: '1px solid #D1D5DB',
                        borderRadius: '8px',
                        cursor: 'pointer'
                      }}
                    >
                      Cancel
                    </button>
                    <button 
                      type="submit" 
                      style={{ 
                        padding: '0.75rem 2rem',
                        fontSize: '1rem',
                        fontWeight: '600',
                        background: '#FF8C42',
                        color: '#FFFFFF',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer'
                      }}
                    >
                      💾 Update Class Fee
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default FeeSchedule;
